/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import isNil from 'lodash.isnil';
import {BaseAsset} from '@ion/core/src/types/entities';
import {DLSTheme} from '@/types/types';
import {FuelPumpIcon} from '../../icons/FuelPumpIcon';
import {dlsAlt, dlsColor} from '../../theme/theme';

export interface AssetFuelLevelProps {
  asset: BaseAsset;
  color?: string;
  classNames?: string[];
  isHidden?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    container : {
      display      : 'flex',
      alignContent : 'center'
    },
    fuelLevel : {
      ...dlsAlt('typography', 'label2'),
      color : dlsColor('grey3')
    }
  };
});

/**
 * AssetFuelLevel component is used to display the fuel level of an asset.
 * 
 * ```typescript
 * import AssetFuelLevel from '@ion/dls/src/components/fuellevel/AssetFuelLevel';
 * <AssetFuelLevel asset={selectedAsset} />
 *  ```
 */
const AssetFuelLevel = ({
  asset,
  color,
  classNames,
  isHidden = false
}: AssetFuelLevelProps) => {
  const classes         = useStyles();
  const {vbFuelPercent} = asset || {};

  if (!isNil(vbFuelPercent) && !isHidden) {
    return (
      <div className={classes.container}>
        <FuelPumpIcon color={color} />
        <span className={classnames(classes.fuelLevel, ...classNames)}>
          {vbFuelPercent}%
        </span>
      </div>
    );
  }
  return null;
};

export default memo(AssetFuelLevel);
